import { useState } from "react";
import { toPng } from "html-to-image";

const isMobileDevice = () =>
  typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

// Teléfono guardado -> número apto para wa.me (heurística AR).
const waPhone = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("549")) return d;
  if (d.startsWith("54")) return "549" + d.slice(2);
  if (d.startsWith("0")) d = d.slice(1);
  if (d.startsWith("15")) d = d.slice(2);
  return "549" + d;
};

// Comparte por WhatsApp la imagen capturada de un nodo (cardRef).
//
// - Escritorio (Mac/Windows): copia la imagen al portapapeles para pegarla en
//   el chat (Chrome/Edge/Safari). Llama clipboard.write con la PROMESA del blob
//   de forma síncrona dentro del click para conservar el "gesto" del usuario
//   (requisito de Safari). Si el portapapeles no soporta imágenes (Firefox),
//   descarga la imagen como último recurso.
// - Celular: usa el compartir nativo, que adjunta la imagen directo.
//
// Tras copiar/descargar marca `waReady` y expone waApp/waWeb para abrir el chat.
export function useWhatsappImageShare({ cardRef, fileName, telefono, caption }) {
  const [generating, setGenerating] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [waReady, setWaReady] = useState(false);

  const tel = waPhone(telefono);
  // App de escritorio/celular (whatsapp://) y WhatsApp Web, para cubrir ambas.
  const waApp = tel ? `whatsapp://send?phone=${tel}` : "whatsapp://";
  const waWeb = tel ? `https://wa.me/${tel}` : "https://web.whatsapp.com/";

  // Un solo botón: intenta abrir la app instalada y, si no se abre (la pestaña
  // sigue enfocada tras un instante), cae a WhatsApp Web automáticamente.
  const abrirWhatsapp = () => {
    let abrioApp = false;
    const marcar = () => { abrioApp = true; };
    window.addEventListener("blur", marcar, { once: true });
    document.addEventListener("visibilitychange", marcar, { once: true });
    window.location.href = waApp;
    window.setTimeout(() => {
      window.removeEventListener("blur", marcar);
      document.removeEventListener("visibilitychange", marcar);
      if (!abrioApp && !document.hidden) {
        window.open(waWeb, "_blank", "noopener");
      }
    }, 1500);
  };

  // Espera a que todas las imágenes del nodo estén cargadas y decodificadas.
  // En Safari/iOS html-to-image suele capturar antes de que el logo/firma
  // terminen de decodificar, y salen en blanco.
  const esperarImagenes = async (node) => {
    const imgs = Array.from(node.querySelectorAll("img"));
    await Promise.all(
      imgs.map(async (img) => {
        try {
          // Si ya terminó de cargar (con éxito o con error) no hay un futuro
          // evento load/error en el que esperar: img.complete queda en true
          // también para una carga fallida, y seguir esperando colgaría la
          // promesa para siempre.
          if (!img.complete) {
            await new Promise((res) => {
              const limpiar = () => {
                img.removeEventListener("load", onDone);
                img.removeEventListener("error", onDone);
              };
              const onDone = () => { limpiar(); res(); };
              img.addEventListener("load", onDone, { once: true });
              img.addEventListener("error", onDone, { once: true });
              // Red de seguridad por si el evento nunca llega.
              window.setTimeout(() => { limpiar(); res(); }, 5000);
            });
          }
          if (img.naturalWidth > 0 && img.decode) await img.decode();
        } catch {
          /* si una imagen falla, seguimos igual */
        }
      })
    );
  };

  // Genera el PNG. En Safari la primera pasada suele omitir las imágenes, así
  // que renderizamos varias veces y nos quedamos con la última (todas las
  // imágenes ya quedan incrustadas por las pasadas previas).
  const generarPng = async () => {
    const node = cardRef.current;
    if (!node) return null;
    await esperarImagenes(node);
    const opts = { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" };
    const pasadas = isMobileDevice() ? 3 : 1;
    let dataUrl = null;
    for (let i = 0; i < pasadas; i++) {
      dataUrl = await toPng(node, opts);
    }
    return dataUrl;
  };

  const downloadDataUrl = (dataUrl) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const descargar = async () => {
    setGenerating(true);
    setFeedback(null);
    try {
      const dataUrl = await generarPng();
      if (dataUrl) {
        downloadDataUrl(dataUrl);
        setFeedback({ type: "success", msg: "Imagen descargada" });
      }
    } catch (e) {
      console.error("Error descargando:", e);
      setFeedback({ type: "error", msg: "No se pudo generar la imagen" });
    } finally {
      setGenerating(false);
    }
  };

  const compartir = async () => {
    setGenerating(true);
    setFeedback(null);
    setWaReady(false);

    if (!isMobileDevice()) {
      let copiado = false;
      try {
        if (navigator.clipboard && window.ClipboardItem) {
          const blobPromise = generarPng().then((u) => fetch(u).then((r) => r.blob()));
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blobPromise })]);
          copiado = true;
        }
      } catch (e) {
        console.warn("No se pudo copiar al portapapeles:", e);
      }
      if (copiado) {
        setFeedback({ type: "success", msg: "Imagen copiada ✅  Abrí el chat y pegala con ⌘/Ctrl + V para enviarla." });
      } else {
        try {
          const dataUrl = await generarPng();
          downloadDataUrl(dataUrl);
          setFeedback({ type: "info", msg: "Imagen descargada. Abrí el chat y arrastrá/adjuntá la imagen." });
        } catch (e) {
          console.error("Error generando imagen:", e);
          setFeedback({ type: "error", msg: "No se pudo generar la imagen" });
          setGenerating(false);
          return;
        }
      }
      setWaReady(true);
      setGenerating(false);
      return;
    }

    // Celular: compartir nativo adjunta la imagen directo a WhatsApp.
    try {
      const dataUrl = await generarPng();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName, { type: "image/png" });
      // Solo la imagen: si pasamos `text`, WhatsApp en iOS lo manda como un
      // mensaje aparte en vez de usarlo como pie de la imagen.
      const shareData = { files: [file] };
      if (navigator.canShare && navigator.canShare(shareData) && navigator.share) {
        await navigator.share(shareData);
        setFeedback({ type: "success", msg: "Compartido" });
      } else {
        downloadDataUrl(dataUrl);
        setWaReady(true);
        setFeedback({ type: "info", msg: "Imagen descargada. Adjuntala en el chat de WhatsApp." });
      }
    } catch (e) {
      if (e?.name === "AbortError") {
        setGenerating(false);
        return;
      }
      console.error("Error compartiendo:", e);
      setFeedback({ type: "error", msg: "No se pudo compartir" });
    } finally {
      setGenerating(false);
    }
  };

  return { generating, feedback, waReady, setWaReady, setFeedback, abrirWhatsapp, descargar, compartir };
}
